import { BulletItem, DealTermsRightParams, GetDealTermsLeftItems, GetOverviewItemsParams } from '../types/types';
import { currencyWithCents, formatDecimal, percentage } from 'utils/numbers';
import RiskBandCriteriaPopup from '../components/RiskBandCriteriaPopup';
import DelinquentTaxesPopup from '../components/DelinquentTaxesPopup';
import { getValueOrDefault } from '../helper';
import StaffSelectPopup from '../components/StaffSelectPopup';

const DEFAULT_STRING = '-';

export const getOverviewItems = ({
  uwRuleVersion,
  applicationSpecialist,
  underwriter,
  htLienPosition,
  principalPaydown,
  totalPaydown,
  rb6Criteria,
  enableFieldEditing,
  trackId,
  highestSingleYearDelinquentTaxes,
  hasRentalResidentialType,
  uwDscr,
  user,
  refetchTrack,
  refetchBlockers,
}: GetOverviewItemsParams): BulletItem[] => {
  const overviewItems = [
    { label: 'Underwriting rule version', value: uwRuleVersion },
    { label: 'Application specialist', value: <StaffSelectPopup isAppSpecialist={true} initialStaff={applicationSpecialist?.user} trackId={trackId} user={user} refetch={refetchTrack}/> },
    { label: 'Underwriter', value: <StaffSelectPopup isAppSpecialist={false} initialStaff={underwriter?.user} trackId={trackId} user={user} refetch={refetchTrack}/> },
    { label: 'HT lien position', value: getValueOrDefault(htLienPosition?.toString()) },
    {
      label: 'Principal paydown',
      value: getValueOrDefault(principalPaydown, DEFAULT_STRING, currencyWithCents),
    },
    { label: 'Total paydown', value: getValueOrDefault(totalPaydown, DEFAULT_STRING, currencyWithCents) },
    {
      label: 'Risk band 6 criteria',
      value: (
        <RiskBandCriteriaPopup
          isEditable={enableFieldEditing}
          rb6Criteria={rb6Criteria}
          trackId={trackId}
          refetch={refetchBlockers}
        />
      ),
    },
    {
      label: 'Highest single year delinquent taxes',
      value: (
        <DelinquentTaxesPopup
          highestSingleYearDelinquentTaxes={highestSingleYearDelinquentTaxes}
          isEditable={enableFieldEditing}
          refetch={refetchBlockers}
          trackId={trackId}
        />
      ),
    },
  ];

  if (hasRentalResidentialType) {
    overviewItems.push({
      label: 'DSCR',
      value: getValueOrDefault(uwDscr, DEFAULT_STRING, v => formatDecimal(v, '0.00')),
    });
  }

  return overviewItems;
};

export const getDealTermsLeftItems = ({
  maximumInvestmentAmount,
  beginningHomeValuation,
  beginningHomeValuationLabel,
  beginningHomeValuationExpStatus,
  lienBalanceTotal,
  pastDueLienBalanceTotal,
  ownersEndingEquity,
  totalEndingEquity,
}: GetDealTermsLeftItems): BulletItem[] => [
  {
    label: 'Maximum investment amount',
    value: getValueOrDefault(maximumInvestmentAmount, DEFAULT_STRING, currencyWithCents),
  },
  {
    label: (
      <>
        <div>{beginningHomeValuationLabel}</div>
        <div>{beginningHomeValuationExpStatus}</div>
      </>
    ),
    value: getValueOrDefault(beginningHomeValuation, DEFAULT_STRING, currencyWithCents),
  },
  {
    label: 'Lien balance total',
    value: getValueOrDefault(lienBalanceTotal, DEFAULT_STRING, currencyWithCents),
  },
  {
    label: 'Past due lien balance total',
    value: getValueOrDefault(pastDueLienBalanceTotal, DEFAULT_STRING, currencyWithCents),
  },
  {
    label: "Owner's ending equity",
    value: getValueOrDefault(ownersEndingEquity, DEFAULT_STRING, v => `${v.toLocaleString()}%`),
  },
  {
    label: 'Total ending equity',
    value: getValueOrDefault(totalEndingEquity, DEFAULT_STRING, v => `${v.toLocaleString()}%`),
  },
];

const percentageFormatter = (v: any) => percentage(v * 100);

export const getDealTermsRightItems = ({
  lossThreshold,
  currentIncomeWeightedRiskBand,
  finalCompositeFico,
  shareUp,
  shareDown,
}: DealTermsRightParams): BulletItem[] => [
  { label: 'Loss threshold', value: getValueOrDefault(lossThreshold, DEFAULT_STRING, percentageFormatter) },
  { label: 'Current income weighted risk band', value: getValueOrDefault(currentIncomeWeightedRiskBand?.toString()) },
  { label: 'Final composite fico', value: getValueOrDefault(finalCompositeFico?.toString()) },
  { label: 'Share up', value: getValueOrDefault(shareUp, DEFAULT_STRING, percentageFormatter) },
  { label: 'Share down', value: getValueOrDefault(shareDown, DEFAULT_STRING, percentageFormatter) },
];
